import React, { createContext, useContext, useState, useEffect } from "react";

interface AuthContextType {
  isLoggedIn: boolean;
  userName?: string;
  authToken?: string;
  loading: boolean; // Add loading state
  login: (token: string, userName: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = "authToken";
const USER_NAME_KEY = "userName";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true); // Add loading state

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUserName = localStorage.getItem(USER_NAME_KEY);

    if (token && storedUserName) {
      setIsLoggedIn(true);
      setAuthToken(token);
      setUserName(storedUserName);
    } else {
      setIsLoggedIn(false);
      setAuthToken(undefined);
      setUserName(undefined);
    }

    setLoading(false); // Set loading to false after initialization
  }, []);

  const login = (token: string, userName: string) => {
    setIsLoggedIn(true);
    setAuthToken(token);
    setUserName(userName);
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(USER_NAME_KEY, userName);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setAuthToken(undefined);
    setUserName(undefined);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_NAME_KEY);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, userName, authToken, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};